import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { env } from '@/lib/env';
import { desktopBridge, type Platform } from '@/lib/platform';

export type SsoAppResult = { code: string } | { error: string };

/** Volta do SSO nos apps: para onde o provedor devolve, como abrir a página dele e como parar de esperar. */
export type AppSsoReturn = {
  returnTo: string;
  open(url: string): Promise<void>;
  /** Para de esperar (desistência, falha, conclusão ou tela fechada): no desktop, fecha também o retorno local. */
  dispose(): void;
};

/** Endereço de volta do Android (esquema do app + host "sso"). */
export const androidSsoReturn = () => `${env.deepLinkScheme}://sso`;

/** O endereço é a volta do SSO (exatamente o retorno, com ou sem consulta)? */
export const isSsoReturnUrl = (url: string, returnTo: string) => url.split('?')[0] === returnTo;

/** Lê o resultado do endereço de volta (consulta ?codigo= ou ?erro=). */
export function parseAppReturn(url: string): SsoAppResult {
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const params = new URLSearchParams(query);
  const code = params.get('codigo');
  const error = params.get('erro');
  return code && !error ? { code } : { error: error || 'LOGIN_EXPIRED' };
}

/** Há uma tela esperando a volta do SSO no Android (as voltas fora disso são "órfãs"). */
let androidWaiting = false;
/** Última volta já entregue a uma tela (não é órfã, seja qual for a ordem dos ouvintes). */
let lastHandled: string | null = null;

/**
 * Prepara a volta do SSO nos apps (T-028b; DEC-074).
 *  - Desktop: o processo principal abre um retorno local em 127.0.0.1 (porta e caminho aleatórios) e a página do
 *    provedor abre no navegador do sistema.
 *  - Android: a página do provedor abre no navegador in-app (Custom Tabs) e a volta chega pelo deep link do app.
 */
export async function appSsoReturn(
  platform: Platform,
  onResult: (result: SsoAppResult) => void,
): Promise<AppSsoReturn> {
  if (platform === 'desktop') {
    const bridge = desktopBridge();
    if (!bridge) throw new Error('O app desktop não está disponível para o login único.');
    const stop = bridge.sso.onResult(onResult);
    const returnTo = await bridge.sso.start();
    let open = true;
    return {
      returnTo,
      open: (url) => bridge.openExternal(url),
      dispose: () => {
        if (!open) return;
        open = false;
        stop();
        // Fecha o retorno local (se a volta ainda não fechou): nada fica ouvindo nem traz a janela depois.
        void bridge.sso.cancel();
      },
    };
  }
  if (platform === 'android') {
    const returnTo = androidSsoReturn();
    androidWaiting = true;
    // A volta abre a MainActivity (singleTask), que tira o navegador in-app da pilha do app: ele sai da frente.
    // (O Browser.close() do plugin no Android traria a aba de volta para frente — por isso não é chamado.)
    const listener = await App.addListener('appUrlOpen', ({ url }) => {
      if (!isSsoReturnUrl(url, returnTo)) return;
      androidWaiting = false;
      lastHandled = url;
      void listener.remove();
      onResult(parseAppReturn(url));
    });
    return {
      returnTo,
      open: (url) => Browser.open({ url, presentationStyle: 'popover' }),
      dispose: () => {
        androidWaiting = false;
        void listener.remove();
      },
    };
  }
  throw new Error('O login único não está disponível nesta plataforma.');
}

/**
 * Volta do SSO que chegou sem uma tela esperando (o Android encerrou o app enquanto o navegador estava aberto: o
 * verificador, que fica só na memória, se perdeu). Avisa para começar de novo — inclusive quando o app abre pela
 * própria volta.
 */
export async function onOrphanSsoReturn(onOrphan: () => void): Promise<() => void> {
  const returnTo = androidSsoReturn();
  const launch = await App.getLaunchUrl().catch(() => undefined);
  if (launch?.url && isSsoReturnUrl(launch.url, returnTo) && !androidWaiting && launch.url !== lastHandled) {
    lastHandled = launch.url;
    onOrphan();
  }
  const listener = await App.addListener('appUrlOpen', ({ url }) => {
    // Espera o ouvinte da tela (se houver) receber primeiro.
    queueMicrotask(() => {
      if (isSsoReturnUrl(url, returnTo) && !androidWaiting && url !== lastHandled) {
        lastHandled = url;
        onOrphan();
      }
    });
  });
  return () => void listener.remove();
}
